import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';

export interface IDraftValue<T> {
    /** Набранное значение: его показывает поле */
    draft: T;
    /** Ввод: поле меняется сразу, наверх значение уходит после паузы */
    change: (next: T) => void;
    /** Отдать неотправленное сразу — уход из поля */
    flush: () => void;
    /** Заменить набранное и отдать сразу — очистка */
    commit: (next: T) => void;
    /** Заменить набранное без отправки и отменить неотправленное: значение владельцу задал он сам */
    replace: (next: T) => void;
}

export interface IDraftValueOptions {
    /** Неотправленное при размонтировании отдаётся, как ушло бы после паузы (иначе теряется) */
    flushOnUnmount?: boolean;
}

/**
 * Черновик поля с отправкой наверх после паузы ввода: владелец значения перерисовывается на паузе, а не на каждый
 * символ. Внешнее значение, отличное от отданного полем последним (шаблон вида, сброс), заменяет набранное и отменяет
 * неотправленное; своё, вернувшееся от владельца, набор с продолжением не сбрасывает. Неотправленное при размонтировании
 * теряется, с `flushOnUnmount` — отдаётся.
 */
export const useDraftValue = <T>(
    value: T,
    onCommit: (value: T) => void,
    delay: number,
    { flushOnUnmount = false }: IDraftValueOptions = {},
): IDraftValue<T> => {
    const [draft, setDraft] = useState(value);
    const sent = useRef(value);
    const send = useDebouncedCallback(
        (next: T) => {
            if (Object.is(next, sent.current)) return;
            sent.current = next;
            onCommit(next);
        },
        { delay, flushOnUnmount },
    );
    useEffect(() => {
        if (Object.is(value, sent.current)) return;
        send.cancel();
        sent.current = value;
        setDraft(value);
    }, [value, send]);
    const change = useCallback(
        (next: T) => {
            setDraft(next);
            send(next);
        },
        [send],
    );
    // flush у send подменяется на каждом вызове — берётся в момент ухода, а не рендера
    const flush = useCallback(() => send.flush(), [send]);
    const commit = useCallback(
        (next: T) => {
            setDraft(next);
            send(next);
            send.flush();
        },
        [send],
    );
    const replace = useCallback(
        (next: T) => {
            send.cancel();
            sent.current = next;
            setDraft(next);
        },
        [send],
    );
    return { draft, change, flush, commit, replace };
};
